import { useState, useRef, useEffect, useMemo } from 'react';

/** Plain-language summary of how far a company or group deviates from the global default. */
export function deviationSummary(c) {
  if (!c) return 'what every company gets unless customized';
  const parts = [];
  if (c.ownWordingCount) parts.push(`${c.ownWordingCount} custom wording`);
  if (c.offCount) parts.push(`${c.offCount} turned off`);
  if (c.inheritsGroupWording) parts.push(`group wording from ${c.parentName}`);
  if (c.isParent) parts.unshift(`${c.memberCount} member ${c.memberCount === 1 ? 'company' : 'companies'}`);
  return parts.length ? parts.join(' · ') : 'matches default';
}

const differs = (c) => c.ownWordingCount + c.offCount > 0 || c.inheritsGroupWording;

/**
 * "Viewing" combobox: the global default, parent groups, then every active company. Search by name
 * or id, optionally only companies that differ from the default. Keyboard: arrows, Enter, Escape.
 */
export default function ViewingPicker({ companies, value, onSelect, loading }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [onlyDiffering, setOnlyDiffering] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  const selected = value ? companies.find((c) => String(c.id) === String(value)) : null;

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const q = query.trim().toLowerCase();
  const { options, groupCount, companyCount, totalCompanies, differingCount } = useMemo(() => {
    const match = (c) =>
      (!q || c.name.toLowerCase().includes(q) || String(c.id).includes(q)) && (!onlyDiffering || differs(c));
    const groups = companies.filter((c) => c.isParent && match(c));
    const members = companies.filter((c) => !c.isParent && match(c));
    const list = [...(q || onlyDiffering ? [] : [null]), ...groups, ...members];
    return {
      options: list,
      groupCount: groups.length,
      companyCount: members.length,
      totalCompanies: companies.filter((c) => !c.isParent).length,
      differingCount: companies.filter((c) => !c.isParent && differs(c)).length,
    };
  }, [companies, q, onlyDiffering]);

  const choose = (c) => {
    onSelect(c ? String(c.id) : '');
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (options[highlight] !== undefined) choose(options[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const renderOption = (c, i) => {
    const isSelected = c ? String(c.id) === String(value) : !value;
    const indicator = c && (c.ownWordingCount + c.offCount > 0
      ? <span className="cx-badge cx-badge-co">custom</span>
      : c.inheritsGroupWording ? <span className="cx-badge cx-badge-par">via group</span> : null);
    return (
      <li
        key={c ? c.id : 'global'}
        id={`cx-ctx-opt-${i}`}
        role="option"
        aria-selected={isSelected}
        className={`cx-ctx-opt${i === highlight ? ' cx-ctx-opt-active' : ''}${isSelected ? ' cx-ctx-opt-selected' : ''}`}
        onMouseDown={(e) => { e.preventDefault(); choose(c); }}
        onMouseEnter={() => setHighlight(i)}
      >
        <span aria-hidden="true">{c ? (c.isParent ? '⬡' : '🏢') : '🌐'}</span>
        <span className="cx-ctx-name">{c ? c.name : 'Global default'}</span>
        {c && <span className="cx-ctx-id">{c.id}</span>}
        {indicator}
        <span className={`cx-ctx-meta${c && !differs(c) && !c.isParent ? ' cx-muted-light' : ''}`}>{deviationSummary(c)}</span>
      </li>
    );
  };

  const firstCompanyIndex = options.length - companyCount;
  const firstGroupIndex = firstCompanyIndex - groupCount;

  return (
    <div className="cx-ctx" ref={wrapRef}>
      <button
        type="button"
        className="cx-ctx-btn"
        onClick={() => { setOpen((o) => !o); setHighlight(0); }}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={loading}
      >
        <span aria-hidden="true">{selected ? (selected.isParent ? '⬡' : '🏢') : '🌐'}</span>
        <span>
          <span className="cx-ctx-btn-name">{loading ? 'Loading companies…' : selected ? selected.name : 'Global default'}</span>{' '}
          {!loading && <span className="cx-ctx-btn-meta">{deviationSummary(selected)}</span>}
        </span>
      </button>
      {open && (
        <div className="cx-ctx-panel">
          <div className="cx-ctx-top">
            <input
              ref={searchRef}
              type="text"
              className="cx-ctx-search"
              placeholder={`Search ${totalCompanies} active companies by name or ID…`}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
              onKeyDown={onKeyDown}
              role="combobox"
              aria-expanded="true"
              aria-controls="cx-ctx-listbox"
              aria-activedescendant={options.length ? `cx-ctx-opt-${highlight}` : undefined}
              autoComplete="off"
            />
            <label className="cx-ctx-filter">
              <input type="checkbox" checked={onlyDiffering} onChange={(e) => { setOnlyDiffering(e.target.checked); setHighlight(0); }} />
              Only companies that differ from the default ({differingCount})
            </label>
          </div>
          <ul className="cx-ctx-list" id="cx-ctx-listbox" role="listbox" aria-label="Viewing">
            {options.flatMap((c, i) => {
              const items = [];
              if (c && c.isParent && i === firstGroupIndex)
                items.push(<li key="sec-groups" role="presentation" className="cx-ctx-sec">Parent groups ({groupCount})</li>);
              if (c && !c.isParent && i === firstCompanyIndex)
                items.push(
                  <li key="sec-companies" role="presentation" className="cx-ctx-sec">
                    Active companies ({companyCount}{q || onlyDiffering ? ` of ${totalCompanies}` : ''})
                  </li>);
              items.push(renderOption(c, i));
              return items;
            })}
            {companyCount === 0 && <li role="presentation" className="cx-ctx-sec cx-ctx-empty">No matching companies</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
